<template>
    <form>
        <ErrorSummary :response-status="responseStatus"/>
        <div class="space-y-1">
            <h3 class="text-xl font-bold leading-6 text-gray-900">StageAgent Profile URL
                <sup class="text-electric-700">PRO</sup></h3>
        </div>

        <div class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4">
            <div class="text-sm font-medium text-clay-800">Your current profile link</div>
            <div class="mt-1 text-sm text-gray-900 sm:text-right sm:mt-0 sm:col-span-2">
                <a class="flex-grow px-2 py-1 rounded-lg text-powder-800 bg-powder-100 hover:bg-powder-50" href="">https://stageagent.com/talent/<span
                        class="font-bold">{{ profile.currentProfileUrl }}</span></a>
            </div>
        </div>

        <div v-if="profile.hasCustomUrl">
            <div class="mt-6">
                <p class="mt-2 text-sm text-clay-800">You have already changed your profile URL. You can only change
                    your profile URL once.</p>
            </div>
        </div>
        <div v-else>

            <SkeletonCard class="mt-4" v-if="isFetching" card-style="long" :num-of-cards=1></SkeletonCard>
            <div class="relative" :class="{ 'pointer-events-none': !isProMember }">
                <!-- PRO Only overlay for non-members -->
                <div v-if="!isProMember"
                     class="absolute inset-0 z-10 bg-white/40 backdrop-blur-[0.5px] flex items-center justify-center text-sm font-semibold text-gray-700">
                    <span class="text-electric-700">PRO Only</span>
                </div>

                <div class="grid grid-cols-4 gap-6 mt-6">
                    <div class="col-span-4">
                        <label class="block text-sm font-medium text-gray-700" for="newUrl">Choose your custom profile
                            URL</label>
                        <div class="flex mt-1 rounded-md shadow-sm">
                <span class="inline-flex items-center px-3 border border-r-0 text-clay-800 border-clay-500 rounded-l-md bg-gray-50 sm:text-sm">
                    https://stageagent.com/talent/
                </span>
                            <input id="newUrl"
                                   v-model="newUrl"
                                   :disabled="!isProMember"
                                   class="flex-1 block w-full min-w-0 rounded-none border-clay-500 focus:ring-powder-500 focus:border-powder-500 rounded-r-md sm:text-sm"
                                   name="newUrl"
                                   type="text">
                        </div>
                        <p class="mt-2 text-sm text-clay-800">Careful! You can only change your profile URL once.</p>
                    </div>
                </div>

                <div class="py-3 mt-4 text-center">
                    <button type="submit"
                            @click.prevent="submitNewProfileUrl"
                            :disabled="!isProMember"
                            :class="[
                  'w-full inline-flex items-center justify-center px-4 py-[calc(theme(spacing.2)-1px)] rounded-full border shadow-md whitespace-nowrap text-base font-medium',
                  isProMember 
                    ? 'bg-clay-950 text-clay-100 hover:bg-clay-900 hover:text-clay-50 border-transparent'
                    : 'bg-gray-300 text-gray-600 cursor-not-allowed'
                ]">
                        Save custom profile URL
                    </button>
                </div>
            </div>


        </div>

    </form>
    <NotificationSuccess :show="updateSuccessful" content="Your changes were successfully saved."
                         title="Successfully saved!"/>

</template>

<script lang="ts" setup>
// @ts-ignore
import {computed, defineComponent, onMounted, Ref, ref} from "vue";
import {client, trackEvent} from '@/shared';
import {
    GetProfileCustomUrl, GetProfileCustomUrlResponse,
    UpdateProfileCustomUrl,
} from "@/dtos";
import {ResponseStatus} from "@servicestack/client";
import ErrorSummary from "@/main/components/ErrorSummary.vue";
import NotificationSuccess from "@/main/components/NotificationSuccess.vue";
import SkeletonCard from "@/main/components/skeletons/SkeletonCard.vue";
import {store} from "@/store";

const updateSuccessful: Ref<Boolean> = ref(false);
const responseStatus = ref(null);
const profile: Ref<GetProfileCustomUrlResponse> = ref({});


const isFetching = ref(true);
onMounted(async () => {
    await client.get<GetProfileCustomUrlResponse>(new GetProfileCustomUrl())
        .then(response => {
            profile.value = response;
            isFetching.value = false;
        });
});

const newUrl = ref("");

const submitNewProfileUrl = async () => {
    updateSuccessful.value = false;

    let request = {
        newUrl: newUrl.value
    };
    await client.post<ResponseStatus>(new UpdateProfileCustomUrl(request))
        .then(response => {
            updateSuccessful.value = true;
            responseStatus.value = null;

            trackEvent('profile_updated', {
                method: 'custom_url_updated',
                url: request.newUrl
            });
        })
        .catch(e => {
            responseStatus.value = e.responseStatus;
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        });
};

const isProMember = computed(() => {
    return store.saUserSession.subscriptionType === 'pro';
});

</script>
<style>

</style>